import React, { useState } from 'react';
import { Card, Typography, Spin, Button, Collapse, Empty, Tag } from 'antd';
import { ProfessionalType } from '../../../types';
import MaterialIcon from '../../Icons/MaterialIcon';
import { useAIAnalysis } from '../../Questionnaire/NewAI/shared/hooks';
import { AIAnalysisResult } from '../../Questionnaire/NewAI/shared/types';
import AIAnalysisResultPrompt1 from './AIAnalysisResultPrompt1';
import AIAnalysisResultPrompt2 from './AIAnalysisResultPrompt2';
import AIAnalysisResultPrompt3 from './AIAnalysisResultPrompt3';

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

interface AIAnalysisResultProps {
  // The professional user, for language preference
  user?: ProfessionalType;
  // The client whose Analysis Results are being viewed
  client: any;
}

const AIAnalysisResultComponent: React.FC<AIAnalysisResultProps> = ({ user, client }) => {
  const [showResults, setShowResults] = useState(false);
  
  // Use the shared hook to fetch AI analysis (with polling enabled by default)
  const { aiAnalysis, loading, fetchAiAnalysis, isPolling } = useAIAnalysis(client);

  // Log component render and props
  console.log('[AIAnalysisResult] Component rendered with props:');
  console.log('- user:', user);
  console.log('- client:', client);
  console.log('- client.id:', client?.id);
  console.log('- aiAnalysis:', aiAnalysis);
  console.log('- isPolling:', isPolling);

  // Determine the language from the professional's profile, default to 'en'
  const _userAny = user as any;
  const detectedLanguage = _userAny?.userAttributes?.language ?? _userAny?.language ?? 'en';
  const lang = detectedLanguage === 'nl' ? 'nl' : 'en';

  const loadingText = {
    en: 'Loading analysis results...',
    nl: 'Analyseresultaten laden...',
  };

  const noResultsText = {
    en: {
      title: 'No Analysis Results Available',
      message: 'Analysis results for this client have not been generated yet.',
      instructions: 'To generate analysis results, please ensure the client has completed the My Health Intake questionnaire, then submit it for analysis in the Questionnaire Responses section.',
    },
    nl: {
      title: 'Geen Analyseresultaten Beschikbaar',
      message: 'Analyseresultaten voor deze cliënt zijn nog niet gegenereerd.',
      instructions: 'Om analyseresultaten te genereren, zorg ervoor dat de cliënt de My Health Intake vragenlijst heeft voltooid en dien deze vervolgens in voor analyse in het gedeelte Vragenlijst Antwoorden.',
    },
  };

  const titles = {
    en: 'Analysis Result',
    nl: 'Analyse Resultaat',
  };

  const handleViewResults = () => {
    console.log('[AIAnalysisResult] handleViewResults called - toggling visibility');
    setShowResults(!showResults);
  };

  const handleRefresh = () => {
    if (client?.firstName && client?.familyName) {
      const clientName = `${client.firstName} ${client.familyName}`;
      console.log('[AIAnalysisResult] Manual refresh triggered');
      fetchAiAnalysis(clientName);
    }
  };

  const renderAnalysisResult = (result: AIAnalysisResult) => {
    // Check prompt type
    const isPrompt2 = result.prompt_id === 'Prompt_2';
    const isPrompt3 = result.prompt_id === 'Prompt_3';
    
    return (
      <Panel
        key="analysis-result"
        header={
          <div>
            <strong>{lang === 'en' ? 'Analysis' : 'Analyse'}</strong>
            {result.prompt_id && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {result.prompt_id.replace('_', ' ')}
              </Tag>
            )}
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {lang === 'en' ? 'Client' : 'Cliënt'}: {result['client-name']}
            </div>
          </div>
        }
      >
        {isPrompt3 ? (
          <AIAnalysisResultPrompt3 
            result={result} 
            lang={lang} 
            clientName={result['client-name']}
            professionalName={result['professional-name']}
          />
        ) : isPrompt2 ? (
          <AIAnalysisResultPrompt2 
            result={result} 
            lang={lang}
            clientName={result['client-name']}
            professionalName={result['professional-name']}
          />
        ) : (
          <AIAnalysisResultPrompt1 result={result} lang={lang} />
        )}
      </Panel>
    );
  };

  const hasResults = !!aiAnalysis;

  return (
    <Card>
      <Title level={5}>{titles[lang]}</Title>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Paragraph>{loadingText[lang]}</Paragraph>
          </div>
        </div>
      ) : hasResults ? (
        <>
          <Paragraph>
            {lang === 'en' 
              ? `Analysis results available for ${aiAnalysis?.['client-name'] || 'this client'}.`
              : `Analyseresultaten beschikbaar voor ${aiAnalysis?.['client-name'] || 'deze cliënt'}.`
            }
            {isPolling && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                {lang === 'en' ? 'Auto-refreshing' : 'Automatisch vernieuwen'}
              </Tag>
            )}
          </Paragraph>
          
          <div style={{ marginBottom: showResults ? 16 : 0 }}>
            <Button 
              type={showResults ? "default" : "primary"}
              onClick={handleViewResults}
              style={{ marginRight: 8 }}
              icon={<MaterialIcon className="material-icons-sharp" icon={showResults ? "visibility_off" : "visibility"} />}
            >
              {showResults 
                ? (lang === 'en' ? 'Hide Results' : 'Verberg Resultaten')
                : (lang === 'en' ? 'Show Results' : 'Toon Resultaten')
              }
            </Button>
            <Button 
              onClick={handleRefresh}
              loading={loading}
              icon={<MaterialIcon className="material-icons-sharp" icon="refresh" />}
            >
              {lang === 'en' ? 'Refresh' : 'Vernieuwen'}
            </Button>
          </div>

          {showResults && aiAnalysis && (
            <Collapse accordion defaultActiveKey={['analysis-result']}>
              {renderAnalysisResult(aiAnalysis)}
            </Collapse>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <Title level={5} style={{ color: '#666', marginBottom: 16 }}>
                  {noResultsText[lang].title}
                </Title>
                <Paragraph style={{ color: '#888', marginBottom: 16 }}>
                  {noResultsText[lang].message}
                </Paragraph>
              </div>
            }
          />
        </div>
      )}
    </Card>
  );
};

export default AIAnalysisResultComponent;
